/**
 * Memory Optimization System for Substrate Processing
 * Provides object pooling, lazy loading, LRU caching, and memory monitoring
 */

import type { SubstrateProfile } from "../../types/substrate";
import type { CMYK } from "../colorEngine";

// Memory monitoring interfaces
export interface MemoryUsage {
  /** Current heap size in MB */
  heapUsed: number;
  /** Total heap allocated in MB */
  heapTotal: number;
  /** External memory in MB */
  external: number;
  /** Number of active objects in pools */
  pooledObjects: number;
  /** Cache hit ratio */
  cacheHitRatio: number;
}

export interface PoolStats {
  /** Total objects created */
  totalCreated: number;
  /** Objects currently in pool */
  available: number;
  /** Objects currently in use */
  inUse: number;
  /** Peak usage */
  peakUsage: number;
  /** Number of times objects were reused from pool */
  reuseCount: number;
}

// Generic object pool interface
interface ObjectPool<T> {
  acquire(): T;
  release(obj: T): void;
  getStats(): PoolStats;
  clear(): void;
}

/**
 * Generic Object Pool Implementation
 * Reuses objects to reduce garbage collection pressure
 */
class GenericObjectPool<T> implements ObjectPool<T> {
  private pool: T[] = [];
  private inUse: Set<T> = new Set();
  private factory: () => T;
  private resetFn?: (obj: T) => void;
  private maxSize: number;
  private stats: PoolStats = {
    totalCreated: 0,
    available: 0,
    inUse: 0,
    peakUsage: 0,
    reuseCount: 0,
  };

  constructor(
    factory: () => T,
    resetFn?: (obj: T) => void,
    maxSize: number = 100,
  ) {
    this.factory = factory;
    this.resetFn = resetFn;
    this.maxSize = maxSize;
  }

  acquire(): T {
    let obj: T;

    if (this.pool.length > 0) {
      obj = this.pool.pop()!;
      this.stats.reuseCount++;
    } else {
      obj = this.factory();
      this.stats.totalCreated++;
    }

    this.inUse.add(obj);
    this.updateStats();
    return obj;
  }

  release(obj: T): void {
    if (!this.inUse.has(obj)) return;

    this.inUse.delete(obj);

    if (this.resetFn) {
      this.resetFn(obj);
    }

    if (this.pool.length < this.maxSize) {
      this.pool.push(obj);
    }

    this.updateStats();
  }

  getStats(): PoolStats {
    return { ...this.stats };
  }

  clear(): void {
    this.pool = [];
    this.inUse.clear();
    this.updateStats();
  }

  private updateStats(): void {
    this.stats.available = this.pool.length;
    this.stats.inUse = this.inUse.size;
    this.stats.peakUsage = Math.max(this.stats.peakUsage, this.stats.inUse);
  }
}

/**
 * CMYK Object Pool
 * Reuses CMYK color objects for calculations
 */
export class CMYKPool {
  private pool: ObjectPool<CMYK>;

  constructor() {
    this.pool = new GenericObjectPool<CMYK>(
      () => ({ c: 0, m: 0, y: 0, k: 0 }),
      (cmyk) => {
        cmyk.c = 0;
        cmyk.m = 0;
        cmyk.y = 0;
        cmyk.k = 0;
      },
      200,
    );
  }

  acquire(): CMYK {
    return this.pool.acquire();
  }

  release(cmyk: CMYK): void {
    this.pool.release(cmyk);
  }

  getStats(): PoolStats {
    return this.pool.getStats();
  }

  clear(): void {
    this.pool.clear();
  }
}

/**
 * Matrix Pool for color transformations
 * Reuses 4x5 transformation matrices
 */
export class MatrixPool {
  private pool: ObjectPool<number[][]>;

  constructor() {
    this.pool = new GenericObjectPool<number[][]>(
      () =>
        Array(4)
          .fill(null)
          .map(() => Array(5).fill(0)),
      (matrix) => {
        for (let i = 0; i < 4; i++) {
          for (let j = 0; j < 5; j++) {
            matrix[i][j] = 0;
          }
        }
      },
      50,
    );
  }

  acquire(): number[][] {
    return this.pool.acquire();
  }

  release(matrix: number[][]): void {
    this.pool.release(matrix);
  }

  getStats(): PoolStats {
    return this.pool.getStats();
  }

  clear(): void {
    this.pool.clear();
  }
}

/**
 * LRU Cache for substrate profiles
 * Keeps most frequently used profiles in memory
 */
export class SubstrateLRUCache<K, V> {
  private cache = new Map<K, V>();
  private maxSize: number;
  private hits = 0;
  private misses = 0;

  constructor(maxSize: number = 5) {
    this.maxSize = maxSize;
  }

  get(key: K): V | undefined {
    const value = this.cache.get(key);

    if (value !== undefined) {
      // Move to end (most recently used)
      this.cache.delete(key);
      this.cache.set(key, value);
      this.hits++;
      return value;
    }

    this.misses++;
    return undefined;
  }

  set(key: K, value: V): void {
    if (this.cache.has(key)) {
      // Update existing
      this.cache.delete(key);
    } else if (this.cache.size >= this.maxSize) {
      // Remove least recently used
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) {
        this.cache.delete(firstKey);
      }
    }

    this.cache.set(key, value);
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }

  delete(key: K): boolean {
    return this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
    this.hits = 0;
    this.misses = 0;
  }

  size(): number {
    return this.cache.size;
  }

  getHitRatio(): number {
    const total = this.hits + this.misses;
    return total > 0 ? this.hits / total : 0;
  }

  keys(): K[] {
    return Array.from(this.cache.keys());
  }
}

/**
 * Substrate Profile Registry with Lazy Loading
 * Stores minimal metadata and loads full profiles on demand
 */
export interface SubstrateMetadata {
  id: string;
  name: string;
  category: string;
  manufacturer: string;
  brightness: number;
  /** Estimated size in bytes */
  estimatedSize: number;
}

export class SubstrateProfileRegistry {
  private metadata: Map<string, SubstrateMetadata> = new Map();
  private profileLoaders: Map<string, () => Promise<SubstrateProfile>> =
    new Map();
  private cache: SubstrateLRUCache<string, SubstrateProfile>;
  private loadingPromises: Map<string, Promise<SubstrateProfile>> = new Map();

  constructor(cacheSize: number = 5) {
    this.cache = new SubstrateLRUCache<string, SubstrateProfile>(cacheSize);
  }

  /**
   * Register a substrate profile with its loader function
   */
  register(
    metadata: SubstrateMetadata,
    loader: () => Promise<SubstrateProfile>,
  ): void {
    this.metadata.set(metadata.id, metadata);
    this.profileLoaders.set(metadata.id, loader);
  }

  /**
   * Get substrate profile metadata
   */
  getMetadata(id: string): SubstrateMetadata | undefined {
    return this.metadata.get(id);
  }

  /**
   * Get all metadata for browsing
   */
  getAllMetadata(): SubstrateMetadata[] {
    return Array.from(this.metadata.values());
  }

  /**
   * Get substrate profile with lazy loading
   */
  async getProfile(id: string): Promise<SubstrateProfile | undefined> {
    // Check cache first
    const cached = this.cache.get(id);
    if (cached) {
      return cached;
    }

    // Check if already loading
    const loadingPromise = this.loadingPromises.get(id);
    if (loadingPromise) {
      return loadingPromise;
    }

    // Check if profile exists
    const loader = this.profileLoaders.get(id);
    if (!loader) {
      return undefined;
    }

    // Load profile
    const promise = loader();
    this.loadingPromises.set(id, promise);

    try {
      const profile = await promise;
      this.cache.set(id, profile);
      return profile;
    } finally {
      this.loadingPromises.delete(id);
    }
  }

  /**
   * Preload commonly used profiles
   */
  async preloadProfiles(ids: string[]): Promise<void> {
    await Promise.all(ids.map((id) => this.getProfile(id)));
  }

  /**
   * Clear cache and loading promises
   */
  clear(): void {
    this.cache.clear();
    this.loadingPromises.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; hitRatio: number; keys: string[] } {
    return {
      size: this.cache.size(),
      hitRatio: this.cache.getHitRatio(),
      keys: this.cache.keys(),
    };
  }
}

/**
 * Memory Monitor
 * Tracks memory usage and provides optimization suggestions
 */
export class MemoryMonitor {
  private isMonitoring = false;
  private monitoringInterval: number | null = null;
  private memoryHistory: MemoryUsage[] = [];
  private maxHistorySize = 100;

  /**
   * Start monitoring memory usage
   */
  startMonitoring(intervalMs: number = 5000): void {
    if (this.isMonitoring) return;

    this.isMonitoring = true;

    // Use appropriate timer based on environment
    if (typeof window !== "undefined") {
      this.monitoringInterval = window.setInterval(() => {
        this.recordMemoryUsage();
      }, intervalMs);
    } else if (typeof setInterval !== "undefined") {
      // Node.js environment
      this.monitoringInterval = setInterval(() => {
        this.recordMemoryUsage();
      }, intervalMs) as unknown as number;
    } else {
      console.warn("Memory monitoring not available - no timer support");
      this.isMonitoring = false;
    }
  }

  /**
   * Stop monitoring memory usage
   */
  stopMonitoring(): void {
    if (!this.isMonitoring) return;

    this.isMonitoring = false;
    if (this.monitoringInterval) {
      // Use appropriate clear function based on environment
      if (typeof window !== "undefined") {
        window.clearInterval(this.monitoringInterval);
      } else if (typeof clearInterval !== "undefined") {
        clearInterval(this.monitoringInterval as unknown as NodeJS.Timeout);
      }
      this.monitoringInterval = null;
    }
  }

  /**
   * Get current memory usage
   */
  getCurrentUsage(): MemoryUsage {
    let heapUsed = 0;
    let heapTotal = 0;
    let external = 0;

    // Try browser performance API (Chrome/Edge)
    if (typeof window !== "undefined" && (window as any).performance?.memory) {
      const perf = (window as any).performance.memory;
      heapUsed = perf.usedJSHeapSize / 1024 / 1024; // Convert to MB
      heapTotal = perf.totalJSHeapSize / 1024 / 1024;
      external = perf.jsHeapSizeLimit / 1024 / 1024;
    } else if (typeof process !== "undefined" && process.memoryUsage) {
      // Node.js environment
      const mem = process.memoryUsage();
      heapUsed = mem.heapUsed / 1024 / 1024;
      heapTotal = mem.heapTotal / 1024 / 1024;
      external = mem.external / 1024 / 1024;
    } else {
      // Fallback - return zeros if memory APIs unavailable
      console.warn("Memory monitoring not available in this environment");
    }

    return {
      heapUsed,
      heapTotal,
      external,
      pooledObjects: 0, // Will be updated by pools
      cacheHitRatio: 0, // Will be updated by cache
    };
  }

  /**
   * Get memory usage history
   */
  getHistory(): MemoryUsage[] {
    return [...this.memoryHistory];
  }

  /**
   * Get memory optimization suggestions
   */
  getSuggestions(): string[] {
    const current = this.getCurrentUsage();
    const suggestions: string[] = [];

    if (current.heapUsed > 100) {
      // > 100MB
      suggestions.push("Consider reducing cache size");
    }

    if (current.heapUsed > 200) {
      // > 200MB
      suggestions.push("Clear object pools to free memory");
      suggestions.push("Reduce maximum pool sizes");
    }

    const recent = this.memoryHistory.slice(-10);
    if (recent.length > 5) {
      const avgUsage =
        recent.reduce((sum, usage) => sum + usage.heapUsed, 0) / recent.length;
      if (avgUsage > current.heapUsed * 1.5) {
        suggestions.push(
          "Memory usage trending downward - consider increasing cache sizes",
        );
      }
    }

    return suggestions;
  }

  /**
   * Clear memory history
   */
  clearHistory(): void {
    this.memoryHistory = [];
  }

  /**
   * Record memory usage (public for testing)
   */
  recordMemoryUsage(): void {
    const usage = this.getCurrentUsage();
    this.memoryHistory.push(usage);

    if (this.memoryHistory.length > this.maxHistorySize) {
      this.memoryHistory.shift();
    }

    // Auto-suggest optimizations if memory is high
    if (usage.heapUsed > 150) {
      console.warn(
        "High memory usage detected:",
        usage.heapUsed.toFixed(2),
        "MB",
      );
      console.warn("Suggestions:", this.getSuggestions());
    }
  }
}

/**
 * Main Memory Optimizer
 * Coordinates all memory optimization components
 */
export class MemoryOptimizer {
  private cmykPool: CMYKPool;
  private matrixPool: MatrixPool;
  private registry: SubstrateProfileRegistry;
  private monitor: MemoryMonitor;
  private isDisposed = false;

  constructor(cacheSize: number = 5) {
    this.cmykPool = new CMYKPool();
    this.matrixPool = new MatrixPool();
    this.registry = new SubstrateProfileRegistry(cacheSize);
    this.monitor = new MemoryMonitor();
  }

  /**
   * Get CMYK object pool
   */
  getCMYKPool(): CMYKPool {
    return this.cmykPool;
  }

  /**
   * Get matrix object pool
   */
  getMatrixPool(): MatrixPool {
    return this.matrixPool;
  }

  /**
   * Get substrate registry
   */
  getRegistry(): SubstrateProfileRegistry {
    return this.registry;
  }

  /**
   * Get memory monitor
   */
  getMonitor(): MemoryMonitor {
    return this.monitor;
  }

  /**
   * Get comprehensive memory usage report
   */
  getMemoryReport(): {
    usage: MemoryUsage;
    cmykPool: PoolStats;
    matrixPool: PoolStats;
    cache: { size: number; hitRatio: number; keys: string[] };
    suggestions: string[];
  } {
    const usage = this.monitor.getCurrentUsage();
    const cmykStats = this.cmykPool.getStats();
    const matrixStats = this.matrixPool.getStats();
    const cacheStats = this.registry.getCacheStats();

    return {
      usage: {
        ...usage,
        pooledObjects: cmykStats.inUse + matrixStats.inUse,
        cacheHitRatio: cacheStats.hitRatio,
      },
      cmykPool: cmykStats,
      matrixPool: matrixStats,
      cache: cacheStats,
      suggestions: this.monitor.getSuggestions(),
    };
  }

  /**
   * Start automatic memory monitoring
   */
  startMonitoring(intervalMs: number = 5000): void {
    this.monitor.startMonitoring(intervalMs);
  }

  /**
   * Stop automatic memory monitoring
   */
  stopMonitoring(): void {
    this.monitor.stopMonitoring();
  }

  /**
   * Optimize memory usage
   */
  optimize(): void {
    // Clear pools
    this.cmykPool.clear();
    this.matrixPool.clear();

    // Clear cache
    this.registry.clear();

    // Force garbage collection if available
    if ((window as any).gc) {
      (window as any).gc();
    }
  }

  /**
   * Dispose all resources
   */
  dispose(): void {
    if (this.isDisposed) return;

    this.stopMonitoring();
    this.cmykPool.clear();
    this.matrixPool.clear();
    this.registry.clear();
    this.monitor.clearHistory();

    this.isDisposed = true;
  }
}

// Global memory optimizer instance
export const memoryOptimizer = new MemoryOptimizer();
